import { Show, createMemo } from "solid-js";
import { Portal } from "solid-js/web";
import { locale, t } from "../i18n";
import { FxCanvas } from "../lib/canvas";
import {
  AFTERGLOW,
  ORIGIN,
  REIGNITE,
  resolveStreakRitual,
} from "../lib/rituals";
import {
  dismissStreakCelebration,
  streakCelebrationSignal,
} from "../lib/streakCelebration";
import { tgHapticImpact } from "../lib/telegram";

function ruPlural(n: number, one: string, few: string, many: string) {
  const n10 = n % 10;
  const n100 = n % 100;
  if (n10 === 1 && n100 !== 11) return one;
  if (n10 >= 2 && n10 <= 4 && (n100 < 12 || n100 > 14)) return few;
  return many;
}

function weekUnit(weeks: number, ru: boolean) {
  if (ru) return ruPlural(weeks, "неделя", "недели", "недель");
  return weeks === 1 ? "week" : "weeks";
}

function daysInARow(days: number, ru: boolean) {
  if (ru) return `${days} ${ruPlural(days, "день", "дня", "дней")} подряд`;
  return days === 1 ? "1 day in a row" : `${days} days in a row`;
}

export default function StreakUpdateOverlay() {
  const celebration = streakCelebrationSignal();
  const data = createMemo(() => celebration());

  const view = createMemo(() => {
    const c = data();
    if (!c) return null;
    const ru = locale() === "ru";
    if (c.kind === "session") {
      return {
        ritual: AFTERGLOW,
        value: String(c.minutes || 0),
        unit: ru ? "мин" : "min",
        title: t("STREAK_SESSION_DONE"),
        footnote: c.title,
      };
    }
    const weeks = c.to;
    const ritual =
      weeks <= 1 && c.from === 0
        ? ORIGIN
        : c.from === 0
          ? REIGNITE
          : resolveStreakRitual(weeks);
    if (ritual.id === "origin") {
      return {
        ritual,
        value: "1",
        unit: weekUnit(1, ru),
        title: t("STREAK_FIRST"),
        footnote: daysInARow(7, ru),
      };
    }
    if (ritual.id === "reignite") {
      return {
        ritual,
        value: String(weeks),
        unit: weekUnit(weeks, ru),
        title: t("STREAK_BACK"),
        footnote: daysInARow(weeks * 7, ru),
      };
    }
    return {
      ritual,
      value: String(weeks),
      unit: weekUnit(weeks, ru),
      title: t("STREAK_UPDATED"),
      footnote: daysInARow(weeks * 7, ru),
    };
  });

  function close() {
    tgHapticImpact("light");
    dismissStreakCelebration();
  }

  return (
    <Show when={view()}>
      {(v) => {
        const draw = v().ritual.fx();
        const wide = v().value.length > 3;
        return (
          <Portal>
            <article
              class="cele"
              data-fx={v().ritual.id}
              role="dialog"
              aria-modal="true"
              aria-labelledby="ritual-title"
              onClick={close}
            >
              <FxCanvas draw={draw} />

              <div class="hero-copy">
                <div class="num" classList={{ wide }}>
                  {v().value}
                </div>
                <div class="unit">{v().unit}</div>
                <h1 id="ritual-title" class="title">
                  {v().title}
                </h1>
                <Show when={v().footnote}>
                  <p class="days">{v().footnote}</p>
                </Show>
              </div>

              <footer class="chrome-bot">
                <span class="replay">{t("STREAK_CONTINUE")}</span>
              </footer>
            </article>
          </Portal>
        );
      }}
    </Show>
  );
}
